/**
 * Create a student account, from the web.
 *
 * *** THREE STEPS, AND THE THIRD IS A GATE ***
 * Details → phone OTP → email code. The student IS signed in after the OTP
 * (a PENDING_VERIFICATION account is refused only when SUSPENDED), so
 * without the third step they would land in the portal with an unverified
 * address — and the meeting invite goes to that address. So this screen
 * holds them until the emailed code is entered. Decided with the user
 * 2026-10-31, option B.
 *
 * *** WHY THE SLUG IS REQUIRED ***
 * One portal deployment serves every institute and carries no app key, so
 * the URL names the tenant — exactly as `/login/<slug>` does. Without one
 * there is nothing to register into, so the bare `/signup` is not a route.
 *
 * *** TWO PROOFS, NEITHER OPTIONAL ***
 * The phone OTP proves the number; the emailed code proves the address.
 * Neither substitutes for the other, and the server promotes the account to
 * ACTIVE only when both are done.
 */

import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ApiError, authApi } from '@/lib/api'
import { rememberEntrance, useAuth } from '@/lib/auth'
import { TextField } from '@/components/ui/Field'
import { Button } from '@/components/ui/Button'
import { AuthBrand } from './AuthBrand'
import { useInstituteBrand } from './useInstituteBrand'
import { PolicyLinksFooter } from '@/components/ui/PolicyLinks'
import styles from './LoginTabs.module.css'

type Step = 'details' | 'otp' | 'email'

/**
 * The stored form of a phone number.
 *
 * *** THE SERVER MATCHES EXACTLY ***
 * `app_user.phone` holds `+919821465264`. Registration writes what it is
 * given, so a bare ten digits here would create an account the OTP sign-in
 * can never find — it strips spaces and hyphens but adds no country code.
 * Same rule as the login form, deliberately duplicated rather than shared,
 * because the two files may diverge on which countries they accept.
 */
function toStoredPhone(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, '')
  if (digits.startsWith('+')) return digits
  if (digits.length === 10) return `+91${digits}`
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`
  return digits
}

export function SignupPage() {
  const { slug } = useParams<{ slug?: string }>()
  const { studentLogin } = useAuth()
  const { brand, loading: brandLoading } = useInstituteBrand(slug)

  // Same reason as the login page: a visitor who does not finish signing up
  // should still be bounced to their own institute's door later.
  useEffect(() => {
    if (slug) rememberEntrance(slug)
  }, [slug])

  const [step, setStep] = useState<Step>('details')

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [emailCode, setEmailCode] = useState('')

  const [maskedPhone, setMaskedPhone] = useState('')
  const [maskedEmail, setMaskedEmail] = useState('')

  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const describe = (caught: unknown, fallback: string): string => {
    if (caught instanceof ApiError) return caught.message || fallback
    return 'Could not reach the server. Check your connection.'
  }

  /* ------------------------------------------------------ step 1: details */
  const handleRegister = async (event: FormEvent) => {
    event.preventDefault()
    if (!slug) return
    setError(null)
    setNotice(null)
    setSubmitting(true)
    try {
      const result = await authApi.registerStudent(
        slug,
        fullName.trim(),
        email.trim(),
        toStoredPhone(phone),
      )
      setMaskedPhone(result.otp_sent_to)
      setMaskedEmail(result.email_verification_sent_to)
      setStep('otp')
      setNotice(`We sent a code to ${result.otp_sent_to}.`)
    } catch (caught) {
      /*
        The server is deliberately specific here — "an account with this
        email or phone already exists. Please sign in." — because a vague
        message would strand a returning student with nowhere to go.
      */
      setError(describe(caught, 'Could not create your account.'))
    } finally {
      setSubmitting(false)
    }
  }

  /* ---------------------------------------------------------- step 2: OTP */
  const handleOtp = async (event: FormEvent) => {
    event.preventDefault()
    if (!slug) return
    setError(null)
    setSubmitting(true)
    try {
      /*
        This signs them in. The account is still PENDING_VERIFICATION, which
        is exactly why the next step can call an authenticated endpoint —
        and exactly why this screen must not let them past it.

        `isAuthenticated` flips here, so the router would normally swap the
        whole tree. It does not, because `/signup/:slug` is also mounted on
        the authenticated side; see AppRoutes.
      */
      await studentLogin(slug, toStoredPhone(phone), otp.trim())
      setStep('email')
      setNotice(`Now enter the code we sent to ${maskedEmail}.`)
    } catch (caught) {
      setError(describe(caught, 'That code is invalid or has expired.'))
    } finally {
      setSubmitting(false)
    }
  }

  /* --------------------------------------------------- step 3: email code */
  const handleEmailCode = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await authApi.verifyMyEmail(emailCode.trim())
      /*
        The account is ACTIVE now. A full reload rather than a route push:
        the session in memory still says PENDING_VERIFICATION, and every
        screen that reads `status` would be working from a stale answer.
      */
      window.location.assign('/sessions')
    } catch (caught) {
      setError(describe(caught, 'That code is invalid or has expired.'))
    } finally {
      setSubmitting(false)
    }
  }

  const resend = async () => {
    setError(null)
    setSubmitting(true)
    try {
      await authApi.resendMyEmailCode()
      setNotice(`We sent a new code to ${maskedEmail}.`)
    } catch (caught) {
      setError(describe(caught, 'Could not send a new code.'))
    } finally {
      setSubmitting(false)
    }
  }

  /*
    *** NO SLUG, NO SIGN-UP ***
    There is no institute to join. Said plainly rather than rendering a form
    that cannot submit.
  */
  if (!slug) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>MentorsConnect</h1>
          <p className={styles.footnote}>
            Use your institute’s own sign-up link to create an account.
          </p>
          <p className={styles.footnote}>
            <Link to="/login">Back to sign in</Link>
          </p>
        </div>
        {/* No institute, so only the platform's own two links. */}
        <PolicyLinksFooter />
      </div>
    )
  }

  /*
    *** "STUDENT", NOT JUST "ACCOUNT" ***
    This form only ever creates a student. A mentor account is made by an
    administrator and there is no self-service path to one, so saying
    "your account" invites a mentor to fill it in and be refused later.
  */
  const subtitle =
    step === 'details'
      ? 'Create your student account'
      : step === 'otp'
        ? 'Confirm your phone'
        : 'Confirm your email'

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <AuthBrand brand={brand} loading={brandLoading} subtitle={subtitle} />

        {step === 'details' && (
          <form onSubmit={handleRegister} className={styles.form} noValidate>
            <TextField
              label="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              autoFocus
              required
              disabled={submitting}
            />
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              disabled={submitting}
              /* Said up front, because it is the reason the address is
                 verified at all and the reason a typo matters. */
              hint="Your meeting invitations are sent here."
            />
            <TextField
              label="Phone number"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10-digit mobile number"
              autoComplete="tel"
              required
              disabled={submitting}
              hint="We will text you a code to confirm it."
            />

            {error && (
              <div className={styles.error} role="alert">
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
              disabled={!fullName || !email || !phone}
            >
              Create account
            </Button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleOtp} className={styles.form} noValidate>
            <TextField
              label="Code sent to your phone"
              inputMode="numeric"
              value={otp}
              /* Digits only, six of them — see the login form's note. */
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="6-digit code"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              required
              disabled={submitting}
              hint={maskedPhone ? `Sent to ${maskedPhone}` : undefined}
            />

            {notice && <p className={styles.footnote}>{notice}</p>}
            {error && (
              <div className={styles.error} role="alert">
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
              disabled={!otp}
            >
              Confirm phone
            </Button>
          </form>
        )}

        {step === 'email' && (
          <form onSubmit={handleEmailCode} className={styles.form} noValidate>
            <TextField
              label="Code sent to your email"
              inputMode="numeric"
              value={emailCode}
              onChange={(e) =>
                setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="6-digit code"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              required
              disabled={submitting}
              hint={maskedEmail ? `Sent to ${maskedEmail}` : undefined}
            />

            {notice && <p className={styles.footnote}>{notice}</p>}
            {error && (
              <div className={styles.error} role="alert">
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
              disabled={!emailCode}
            >
              Finish
            </Button>

            {/*
              *** THE ONLY WAY OUT OF A FAILED SEND ***
              Mail that never arrived, a code that expired. Without this the
              student is stuck on this screen with no action available, which
              is a worse trap than the one the gate exists to prevent.
            */}
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={resend}
              disabled={submitting}
            >
              Send the code again
            </Button>
          </form>
        )}

        <p className={styles.footnote}>
          {step === 'details' ? (
            <>
              Already have an account?{' '}
              <Link to={`/login/${slug}`}>Sign in</Link>
            </>
          ) : (
            'Both steps are required before you can use your account.'
          )}
        </p>

        {/* The institute's own policies — someone creating an account is
            precisely who needs to read them. */}
        <PolicyLinksFooter slug={slug} />
      </div>
    </div>
  )
}
